import axios, { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { validateDamAssets } from '@/pages/dam/api/damAssetValidation';
import { linkItem } from '@/pages/dam/api/linkedItems.queries';
import { consumeWithVirusScanRetry, deleteUpload, stageFile } from '@/services/stagedUpload';
import { relativeUrlResolver } from '@/services/utils';
import type { PersistedItem } from '@/types/DataModel';

import { type DamUploadFile, isHiddenUpload, isSupportedUpload, isUploaded, toUploadValidation } from './damUploadFiles';

const VALIDATION_BATCH_DELAY_MS = 300;

interface DamCreateResult {
    created: number;
    /** Files rejected because their content already exists (409). */
    duplicates: number;
    failed: number;
    /** Created assets whose product links could not all be saved. */
    linkFailures: number;
}

const readDimensions = (file: File): Promise<DamUploadFile['dimensions']> => {
    if (!file.type.startsWith('image/')) {
        return Promise.resolve(undefined);
    }
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            resolve({ width: image.naturalWidth, height: image.naturalHeight });
            URL.revokeObjectURL(url);
        };
        image.onerror = () => {
            resolve(undefined);
            URL.revokeObjectURL(url);
        };
        image.src = url;
    });
};

const createAsset = async (uploadId: number, category: string | undefined, values: Record<string, unknown>) =>
    (await consumeWithVirusScanRetry(() => axios.post<{ primaryKey__: string }>(
        relativeUrlResolver('/api/v2/assets/'),
        { ...values, ...(category ? { category__: category } : {}) },
        { params: { uploadId } },
    ))).data;

/**
 * Files of the upload wizard (D-046): each file is staged on its own, one after the other, with
 * progress; nothing is created before `create`. Removing a row or discarding the wizard deletes
 * its staged upload. Product links and asset data are kept per file until `create` stores them.
 */
function useDamUpload(category: string | undefined) {
    const [files, setFiles] = useState<DamUploadFile[]>([]);
    const filesRef = useRef<DamUploadFile[]>([]);
    const nextId = useRef(0);
    const queue = useRef<string[]>([]);
    const isDraining = useRef(false);
    const controllers = useRef(new Map<string, AbortController>());
    const pendingValidation = useRef(new Set<string>());
    const validationTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const update = useCallback((change: (current: DamUploadFile[]) => DamUploadFile[]) => {
        filesRef.current = change(filesRef.current);
        setFiles(filesRef.current);
    }, []);

    const patch = useCallback((id: string, changes: Partial<DamUploadFile>) => {
        update((current) => current.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry)));
    }, [update]);

    const runValidation = useCallback(async () => {
        const ids = new Set(pendingValidation.current);
        pendingValidation.current.clear();
        const sent = filesRef.current.filter((entry) => ids.has(entry.id) && isUploaded(entry));
        if (!category || sent.length === 0) {
            return;
        }
        try {
            const results = await validateDamAssets(sent.map((entry) => ({ ...entry.values, category__: category })));
            update((current) => current.map((entry) => {
                const index = sent.findIndex((candidate) => candidate.id === entry.id);
                const result = index >= 0 ? results[index] : undefined;
                if (!result || entry.values !== sent[index].values) {
                    return entry;
                }
                return { ...entry, validation: toUploadValidation(entry.values, result.validations ?? {}) };
            }));
        } catch {
            // The files keep their previous result; the next change validates them again.
        }
    }, [category, update]);

    /** Validates uploads that finish together and bulk edits in one request (D-051). */
    const scheduleValidation = useCallback((ids: string[]) => {
        ids.forEach((id) => pendingValidation.current.add(id));
        clearTimeout(validationTimer.current);
        validationTimer.current = setTimeout(() => void runValidation(), VALIDATION_BATCH_DELAY_MS);
    }, [runValidation]);

    useEffect(() => {
        scheduleValidation(filesRef.current.filter(isUploaded).map((entry) => entry.id));
    }, [scheduleValidation]);

    const stage = useCallback(async (id: string) => {
        const entry = filesRef.current.find((candidate) => candidate.id === id);
        if (!entry) {
            return;
        }
        const controller = new AbortController();
        controllers.current.set(id, controller);
        patch(id, { status: 'uploading', progress: 0, startedAt: Date.now(), error: undefined, uploadId: undefined });
        try {
            const { uploadId } = await stageFile({
                file: entry.file,
                signal: controller.signal,
                onCreated: (createdId) => patch(id, { uploadId: createdId }),
                onProgress: (progress) => patch(id, { progress }),
            });
            patch(id, { status: 'success', progress: 100, uploadId });
            scheduleValidation([id]);
        } catch {
            if (!controller.signal.aborted) {
                patch(id, { status: 'error', error: 'failed' });
            }
        } finally {
            controllers.current.delete(id);
        }
    }, [patch, scheduleValidation]);

    const drain = useCallback(async () => {
        if (isDraining.current) {
            return;
        }
        isDraining.current = true;
        while (queue.current.length > 0) {
            const id = queue.current.shift() as string;
            await stage(id);
        }
        isDraining.current = false;
    }, [stage]);

    const enqueue = useCallback((ids: string[]) => {
        queue.current.push(...ids);
        void drain();
    }, [drain]);

    const addFiles = useCallback((added: File[]) => {
        const entries: DamUploadFile[] = added.filter((file) => !isHiddenUpload(file)).map((file) => {
            nextId.current += 1;
            const supported = isSupportedUpload(file);
            return {
                id: `upload-${nextId.current}`,
                file,
                status: supported ? 'uploading' : 'error',
                progress: 0,
                error: supported ? undefined : 'format',
                items: [],
                values: {},
            };
        });
        update((current) => [...current, ...entries]);
        entries.forEach((entry) => {
            void readDimensions(entry.file).then((dimensions) => dimensions && patch(entry.id, { dimensions }));
        });
        enqueue(entries.filter((entry) => !entry.error).map((entry) => entry.id));
    }, [enqueue, patch, update]);

    const retry = useCallback((id: string) => {
        patch(id, { status: 'uploading', progress: 0, error: undefined });
        enqueue([id]);
    }, [enqueue, patch]);

    const discardStaged = (entries: DamUploadFile[]) => {
        entries.forEach((entry) => {
            controllers.current.get(entry.id)?.abort();
            queue.current = queue.current.filter((queued) => queued !== entry.id);
            if (entry.uploadId !== undefined) {
                void deleteUpload(entry.uploadId).catch(() => undefined);
            }
        });
    };

    const remove = useCallback((ids: string[]) => {
        discardStaged(filesRef.current.filter((entry) => ids.includes(entry.id)));
        update((current) => current.filter((entry) => !ids.includes(entry.id)));
    }, [update]);

    const discardAll = useCallback(() => {
        discardStaged(filesRef.current);
        update(() => []);
    }, [update]);

    const setItems = useCallback((ids: string[], change: (current: PersistedItem[]) => PersistedItem[]) => {
        update((current) => current.map((entry) => (ids.includes(entry.id) ? { ...entry, items: change(entry.items) } : entry)));
    }, [update]);

    const setValues = useCallback((ids: string[], values: Record<string, unknown>) => {
        update((current) => current.map((entry) => (ids.includes(entry.id) ? { ...entry, values: { ...entry.values, ...values } } : entry)));
        scheduleValidation(ids);
    }, [scheduleValidation, update]);

    /**
     * Creates one asset per uploaded file with its values, then links its products. Created files
     * leave the wizard; duplicates and failures stay with their error.
     */
    const create = useCallback(async (onProgress: (done: number, total: number) => void): Promise<DamCreateResult> => {
        const result: DamCreateResult = { created: 0, duplicates: 0, failed: 0, linkFailures: 0 };
        const pending = filesRef.current.filter(isUploaded);
        for (const [index, entry] of pending.entries()) {
            try {
                const asset = await createAsset(entry.uploadId as number, category, entry.values);
                result.created += 1;
                const links = await Promise.allSettled(entry.items.map((item) => linkItem({
                    primaryKey: asset.primaryKey__,
                    itemPrimaryKey: item.primaryKey__,
                })));
                if (links.some((link) => link.status === 'rejected')) {
                    result.linkFailures += 1;
                }
                update((current) => current.filter((candidate) => candidate.id !== entry.id));
            } catch (error) {
                const isDuplicate = isAxiosError(error) && error.response?.status === 409;
                result[isDuplicate ? 'duplicates' : 'failed'] += 1;
                patch(entry.id, { error: isDuplicate ? 'duplicate' : 'create' });
            }
            onProgress(index + 1, pending.length);
        }
        return result;
    }, [category, patch, update]);

    useEffect(() => () => {
        clearTimeout(validationTimer.current);
        discardStaged(filesRef.current);
    }, []);

    return { files, addFiles, retry, remove, discardAll, setItems, setValues, create };
}

export { useDamUpload };
export type { DamCreateResult };
